import { FC, memo, useEffect, useRef } from 'react';
import { GeolocationCoords } from 'app/types/common';
import { YandexMarker } from '../model/types';
import { getBoxDiscountedPriceAmount, getBoxPriceAmountWithVat } from 'entities/Box';
import { UserAccountType } from 'entities/User';
import { renderBalloonContent } from '../lib/helpers/renderBalloonContent';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface YandexMapMarkerProps {
  map: any;
  markerData: YandexMarker;
  selectedMarkerId?: Nullable<string>;
  selectMarker?: (latitude: number, longitude: number, markerId: string) => void;
  onSelectMarker?: (markerCoords: GeolocationCoords) => void;
  withMarkerPopups?: boolean;
  currencySymbol?: string;
  showPriceWithVat?: boolean;
  loggedUserAccountType?: UserAccountType;
  onSelectWarehouseAndProceed?: (markerId: string) => void;
  isClickable?: boolean;
}

export const YandexMapMarker: FC<YandexMapMarkerProps> = memo((props) => {
  const {
    map,
    markerData,
    selectedMarkerId,
    selectMarker,
    onSelectMarker,
    withMarkerPopups,
    currencySymbol = '₽',
    showPriceWithVat = false,
    loggedUserAccountType,
    onSelectWarehouseAndProceed,
    isClickable = true,
  } = props;

  const markerRef = useRef<any>(null);

  // Create marker when map is available
  useEffect(() => {
    if (!map || !window.ymaps) return;

    const { ymaps } = window;
    const position = [markerData.position.lat, markerData.position.lng];

    // Create custom balloon content only when clickable and popups enabled
    let balloonContent: string | undefined;
    if (isClickable && markerData.warehouseData && withMarkerPopups) {
      // Calculate discounted price
      const discountedPrice = getBoxDiscountedPriceAmount({
        warehouseDiscounts: markerData.warehouseData.warehouseDiscounts,
        boxPrice: markerData.warehouseData.minPrice,
      });

      const discountedPriceWithVat = getBoxPriceAmountWithVat(discountedPrice, markerData.warehouseData, loggedUserAccountType);

      const finalPrice = showPriceWithVat
        ? markerData.warehouseData.minPriceWithPersonalVat
        : markerData.warehouseData.minPriceWithBusinessVat;

      // Use helper function to render balloon content
      balloonContent = renderBalloonContent(markerData.warehouseData, finalPrice, currencySymbol, markerData.id);

      // Add global function for button click
      const functionName = `selectWarehouseMarker_${markerData.id.replace(/[^a-zA-Z0-9]/g, '_')}`;
      (window as any)[functionName] = () => {
        onSelectWarehouseAndProceed?.(markerData.id);
      };
    }

    // Build options depending on clickability
    const placemarkOptions: Record<string, unknown> = {
      preset: selectedMarkerId === markerData.id ? 'islands#redDotIcon' : 'islands#blueDotIcon',
      balloonAutoPan: true,
      balloonCloseButton: true,
      balloonPanelMaxMapArea: 0,
    };

    if (!isClickable) {
      placemarkOptions.balloonAutoPan = false;
      placemarkOptions.balloonCloseButton = false;
      // prevent any balloon on click
      // Use known option keys tolerated by ymaps; avoid TS suppression
      (placemarkOptions as any).openBalloonOnClick = false;
      (placemarkOptions as any).hasBalloon = false;
      (placemarkOptions as any).interactivityModel = 'default#opaque';
      placemarkOptions.cursor = 'default';
    }

    // Create marker
    const marker = new ymaps.Placemark(
      position,
      isClickable
        ? {
            hintContent: markerData.labelHeading,
            balloonContent,
          }
        : {},
      placemarkOptions,
    );

    // Add click handler if clickable
    if (isClickable) {
      marker.events.add('click', () => {
        selectMarker?.(markerData.position.lat, markerData.position.lng, markerData.id);
      });
    }

    // Add to map
    map.geoObjects.add(marker);
    markerRef.current = marker;

    return () => {
      if (markerRef.current) {
        map.geoObjects.remove(markerRef.current);
      }
      // Clean up global function
      if ((window as any).selectWarehouseMarker) {
        delete (window as any).selectWarehouseMarker;
      }
    };
  }, [
    map,
    markerData,
    selectedMarkerId,
    selectMarker,
    withMarkerPopups,
    currencySymbol,
    showPriceWithVat,
    loggedUserAccountType,
    isClickable,
    onSelectWarehouseAndProceed,
  ]);

  // Update marker appearance when selection changes
  useEffect(() => {
    if (!markerRef.current || !window.ymaps) return;

    const preset = selectedMarkerId === markerData.id ? 'islands#redDotIcon' : 'islands#blueDotIcon';
    markerRef.current.options.set('preset', preset);
  }, [markerData.id, selectedMarkerId]);

  // Handle marker selection callback
  useEffect(() => {
    if (markerData.id === selectedMarkerId) {
      onSelectMarker?.({ lat: markerData.position.lat, lng: markerData.position.lng });
    }
  }, [markerData, onSelectMarker, selectedMarkerId]);

  return null; // Yandex Maps markers are rendered by the API
});
